// receiptManagement.js — the admin's receipt register and correction desk.
//
// Rewired onto the money engine (spec §13 step 4). What changed, and why:
//
//  • IT NO LONGER READS `transactions`. Migration 0008 renames that table to
//    `transactions_legacy_20260727`; every query here would have thrown at
//    runtime the moment the migration applied. Receipts are projections of
//    `money_ledger` now — a sale, plus whatever corrections point at it.
//
//  • EVERY READ PAGINATES (audit C6). `supabase-js` returns a truncated 1000-row
//    page WITHOUT an error, and at the documented 500–1000 transactions/month the
//    old unbounded `.select()` silently dropped everything past the first month.
//    Silent truncation is wrong money and is forbidden (spec §8).
//
//  • VOID AND DISCOUNT ARE `record_void` / `record_discount` RPCs — admin-only,
//    ONLINE-ONLY, synchronous, NEVER queued (spec §6). They do not touch the
//    outbox: an offline correction has no meaning, because the rules it must be
//    checked against (one active void, one active discount, void = sale net of
//    discount) live on the server under an advisory lock. The refusal code comes
//    back through `messageFor()` in French.
//
//  • THE MANUAL STOCK RESTORE IS DELETED IN FULL (audit H5). It guarded on
//    `drug_id && quantity`, columns populated only when the cart had exactly ONE
//    line, so voiding a 5-drug sale reversed the money and lost the drugs from
//    inventory forever. It was also a read-modify-write off `drugs.current_stock`
//    — a column migration 0008 DROPS — with C2's race and no guard against
//    restoring twice. Voiding restores stock because stock is a FOLD over the
//    ledger (spec §4.1) and `v_stock_movements` restores EVERY line of the target
//    sale. There is nothing left here to get wrong.
//
//  • THE REASON IS REQUIRED AND ACTUALLY PERSISTED (audit M3). It used to be
//    collected with `prompt()` and thrown away — nobody could tell who voided a
//    50,000 receipt or why. It is now a `money_ledger.reason` on the correction
//    event, shown on the receipt with the author and the timestamp.
//
//  • THE DISCOUNT DIALOG'S ORIGINAL-AMOUNT FIELD IS POPULATED (audit M3). It was
//    declared in the markup and never filled, so the admin discounted blind.
//
//  • A RECEIPT SHOWS THE SALE, THE DISCOUNT AND THE VOID AS SEPARATE LINES. The
//    ledger has no `status` column and never mutates a total (spec §3.1); this
//    screen must not invent one, or the record of what was originally charged is
//    destroyed exactly as the old `status TEXT` model destroyed it.
//
//  • EVERY INTERPOLATED STRING IS ESCAPED (audit M7). Patient and item names came
//    from a cashier's keyboard and render HERE, in the ADMIN's session — the one
//    account that can void. Row actions are bound with `addEventListener` and a
//    dataset id rather than an `onclick="…('${id}')"` attribute.

import { supabase } from './supabaseClient.js';
import { showStatus, formatCurrency, formatDateTime } from './utils.js';

import { db } from './db/schema.js';
import { getAllEvents, fetchAllPages } from './db/index.js';
import { getEngine } from './ui/engine-boot.js';
import { messageFor, messageForError } from './ui/engine-messages.js';
import { dedupeById, formatReceiptNo } from './money/index.js';

/**
 * Escape text destined for an `innerHTML` template (audit M7).
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Parse a user-typed FCFA amount as a WHOLE number, or `null`.
 *
 * Deliberately not `parseFloat` (audit "Low": NaN slipped past `<= 0` guards, and
 * `parseFloat('12abc')` is 12). XAF has no subunit (invariant 5): a decimal in a
 * money field is a typo, and the server's numeric gate would park it as
 * `amount-must-be-positive-integer` anyway. Refuse at the keyboard instead.
 * @param {*} raw
 * @returns {number|null}
 */
function parseWholeFcfa(raw) {
    const text = String(raw == null ? '' : raw).trim();
    if (!/^\d{1,9}$/.test(text)) return null;
    const n = Number(text);
    return Number.isSafeInteger(n) ? n : null;
}

/** Corrections are online-only (spec §6). One sentence, one place. */
const ONLINE_ONLY =
    'Cette opération exige une connexion au serveur : une annulation ou une remise '
    + 'ne peut jamais être mise en file d\'attente. Rétablissez le réseau, puis recommencez.';

/** Human label for a ledger channel. */
const CHANNEL_LABEL = Object.freeze({
    regie: 'Régie (services de l\'État)',
    pharmacy: 'Pharmacie'
});

export const ReceiptManagement = {
    // Cache DOM elements
    elements: {},

    /** @type {Map<string, object>} saleId → {sale, discount, void} */
    receipts: new Map(),
    /** @type {Map<string, string>} user id → email */
    userEmails: new Map(),
    /** The receipt currently open in the details modal. */
    currentReceipt: null,
    /** Where the last load came from — shown to the reader, never hidden. */
    lastSource: null,

    // Initialize the module
    init() {
        this.elements = {
            modal: document.getElementById('receipt-management-modal'),
            manageBtn: document.getElementById('manage-receipts-btn'),
            closeModalBtn: document.getElementById('close-receipt-modal'),
            tableBody: document.getElementById('receipts-table-body'),
            sourceNotice: document.getElementById('receipts-source-notice'),
            // Filters
            filterType: document.getElementById('filter-type'),
            filterStatus: document.getElementById('filter-status'),
            filterDate: document.getElementById('filter-date'),
            searchInput: document.getElementById('receipt-search'),
            // Receipt details modal
            detailsModal: document.getElementById('receipt-details-modal'),
            closeDetailsBtn: document.getElementById('close-receipt-details'),
            receiptContent: document.getElementById('receipt-content'),
            // Action buttons
            printBtn: document.getElementById('print-receipt-btn'),
            emailBtn: document.getElementById('email-receipt-btn'),
            voidBtn: document.getElementById('void-receipt-btn'),
            discountBtn: document.getElementById('apply-discount-btn'),
            // Discount form
            discountModal: document.getElementById('discount-modal'),
            closeDiscountBtn: document.getElementById('close-discount-modal'),
            discountForm: document.getElementById('discount-form'),
            discountReceiptId: document.getElementById('discount-receipt-id'),
            discountOriginalAmount: document.getElementById('discount-original-amount'),
            discountAmount: document.getElementById('discount-amount'),
            discountReason: document.getElementById('discount-reason'),
            discountHint: document.getElementById('discount-hint'),
            cancelDiscountBtn: document.getElementById('cancel-discount'),
            // Void form
            voidModal: document.getElementById('void-modal'),
            closeVoidBtn: document.getElementById('close-void-modal'),
            voidForm: document.getElementById('void-form'),
            voidReceiptId: document.getElementById('void-receipt-id'),
            voidAmount: document.getElementById('void-amount'),
            voidReason: document.getElementById('void-reason'),
            voidHint: document.getElementById('void-hint'),
            cancelVoidBtn: document.getElementById('cancel-void')
        };

        this.setupEventListeners();

        // `DeviceProvisioning.init()` used to be called here as a stopgap. It now lives
        // in `app.js`'s role-aware module list, alongside the conflict queue and the day
        // close, so device provisioning no longer depends on whether the receipts screen
        // happened to be initialised.

        console.log('ReceiptManagement module initialized');
    },

    setupEventListeners() {
        this.elements.manageBtn?.addEventListener('click', () => this.openModal());

        this.elements.closeModalBtn?.addEventListener('click', () => this.closeModal());
        this.elements.closeDetailsBtn?.addEventListener('click', () => this.closeDetailsModal());
        this.elements.closeDiscountBtn?.addEventListener('click', () => this.closeDiscountModal());
        this.elements.closeVoidBtn?.addEventListener('click', () => this.closeVoidModal());

        this.elements.modal?.addEventListener('click', (e) => {
            if (e.target === this.elements.modal) this.closeModal();
        });
        this.elements.detailsModal?.addEventListener('click', (e) => {
            if (e.target === this.elements.detailsModal) this.closeDetailsModal();
        });
        this.elements.discountModal?.addEventListener('click', (e) => {
            if (e.target === this.elements.discountModal) this.closeDiscountModal();
        });
        this.elements.voidModal?.addEventListener('click', (e) => {
            if (e.target === this.elements.voidModal) this.closeVoidModal();
        });

        // Filters are applied to the ALREADY-LOADED set. The old code re-queried the
        // database on every keystroke of the search box.
        this.elements.filterType?.addEventListener('change', () => this.renderTable());
        this.elements.filterStatus?.addEventListener('change', () => this.renderTable());
        this.elements.filterDate?.addEventListener('change', () => this.renderTable());
        this.elements.searchInput?.addEventListener('input', () => this.renderTable());

        // Delegated row actions — no `onclick="…('${id}')"` attribute anywhere, so a
        // patient named `'); alert(1); //` is inert (audit M7).
        this.elements.tableBody?.addEventListener('click', (e) => {
            const button = e.target instanceof Element ? e.target.closest('[data-sale-id]') : null;
            if (button) this.viewReceipt(button.getAttribute('data-sale-id'));
        });

        this.elements.printBtn?.addEventListener('click', () => this.printReceipt());
        this.elements.emailBtn?.addEventListener('click', () => this.emailReceipt());
        this.elements.voidBtn?.addEventListener('click', () => this.showVoidForm());
        this.elements.discountBtn?.addEventListener('click', () => this.showDiscountForm());

        this.elements.discountForm?.addEventListener('submit', (e) => this.handleDiscountSubmit(e));
        this.elements.cancelDiscountBtn?.addEventListener('click', () => this.closeDiscountModal());
        this.elements.voidForm?.addEventListener('submit', (e) => this.handleVoidSubmit(e));
        this.elements.cancelVoidBtn?.addEventListener('click', () => this.closeVoidModal());
    },

    async openModal() {
        this.elements.modal?.classList.remove('hidden');
        await this.loadReceipts();
    },

    closeModal() {
        this.elements.modal?.classList.add('hidden');
    },

    closeDetailsModal() {
        this.elements.detailsModal?.classList.add('hidden');
        this.currentReceipt = null;
    },

    closeDiscountModal() {
        this.elements.discountModal?.classList.add('hidden');
        this.elements.discountForm?.reset();
    },

    closeVoidModal() {
        this.elements.voidModal?.classList.add('hidden');
        this.elements.voidForm?.reset();
    },

    // ---- loading ----------------------------------------------------------

    /**
     * Load every ledger event and index it as receipts.
     *
     * THE READ IS NOT FILTERED BY DATE, CHANNEL OR KIND, and that is a rule rather
     * than laziness (spec §8): a `void` carries neither the channel nor the
     * business date of the sale it cancels — it is authored by an admin, on another
     * device, usually on another day. Any server-side filter on those dimensions
     * would drop corrections out of the set and A VOIDED SALE WOULD SILENTLY
     * RE-COUNT as fully paid. Membership and grouping are fold-side only.
     *
     * The server copy and this device's local mirror are merged, so a sale still
     * sitting in the outbox is visible here rather than absent (audit H6). Dedupe
     * is first-occurrence-wins on the ledger id, and the server rows come first.
     */
    async loadReceipts() {
        if (!this.elements.tableBody) return;
        this.elements.tableBody.innerHTML =
            '<tr><td colspan="8" class="text-center text-muted">Chargement des reçus…</td></tr>';

        try {
            const engine = getEngine();
            const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;

            /** @type {object[]} */
            let serverEvents = [];
            let source = 'device';
            if (engine && engine.transport && online) {
                try {
                    // Paginated to completion inside the transport — audit C6.
                    serverEvents = await engine.transport.fetchAllLedger();
                    source = 'server';
                } catch (error) {
                    console.warn('Lecture du registre serveur impossible, repli sur le miroir local:', error);
                }
            }

            const localEvents = await getAllEvents(db).catch(() => []);
            const events = dedupeById(serverEvents.concat(localEvents));

            this.lastSource = source;
            this.indexReceipts(events);
            await this.loadUserEmails();
            this.renderSourceNotice(source, events.length);
            this.renderTable();
        } catch (error) {
            console.error('Erreur au chargement des reçus:', error);
            showStatus(`Chargement des reçus impossible : ${error.message}`, true);
            this.elements.tableBody.innerHTML =
                '<tr><td colspan="8" class="text-center" style="color: var(--danger-600);">Erreur de chargement des reçus</td></tr>';
        }
    },

    /**
     * Build `saleId → {sale, discount, void}` from a flat event list.
     *
     * A correction that names a sale we do not hold is NOT dropped: it is kept in
     * `orphanCorrections` and reported, because a correction whose target is
     * missing is money that moved and a set that is incomplete — silence there is
     * the pre-rewrite behaviour this rewrite exists to end.
     * @param {object[]} events
     */
    indexReceipts(events) {
        /** @type {Map<string, object>} */
        const byId = new Map();
        this.receipts = new Map();
        this.orphanCorrections = [];

        for (const event of events) {
            if (!event || event.id == null) continue;
            byId.set(event.id, event);
            if (event.kind === 'sale') {
                this.receipts.set(event.id, { sale: event, discount: null, void: null });
            }
        }

        for (const event of events) {
            if (!event || (event.kind !== 'void' && event.kind !== 'discount')) continue;
            const target = this.receipts.get(event.reverses_id);
            if (!target) {
                this.orphanCorrections.push(event);
                continue;
            }
            // One active void and one active discount per sale (invariant 10),
            // enforced server-side by two partial unique indexes. If a second one
            // ever reaches this client, keep the FIRST and surface the other rather
            // than quietly overwriting: a correction that vanishes from a screen is
            // exactly the class of defect this module is being rewritten for.
            if (event.kind === 'void') {
                if (target.void) this.orphanCorrections.push(event);
                else target.void = event;
            } else if (target.discount) {
                this.orphanCorrections.push(event);
            } else {
                target.discount = event;
            }
        }
    },

    /**
     * The `id → email` map used to say who collected, recorded, voided or discounted.
     *
     * NOT a PostgREST join. `reporting.js:250` dereferenced `t.users.email`
     * unguarded and one null join killed the whole report (audit L7); a separate map
     * with an explicit fallback cannot do that.
     */
    async loadUserEmails() {
        try {
            const rows = await fetchAllPages((from, to) => supabase
                .from('users')
                .select('id, email')
                .order('id', { ascending: true })
                .range(from, to));
            this.userEmails = new Map(rows.map(u => [u.id, u.email]));
        } catch (error) {
            console.warn('Liste des comptes indisponible:', error);
            // An empty map is honest: every name renders as « compte inconnu ».
            this.userEmails = new Map();
        }
    },

    /**
     * @param {string|null|undefined} userId
     * @returns {string} Never throws, never renders `undefined` (audit L7).
     */
    emailFor(userId) {
        if (!userId) return 'compte inconnu';
        return this.userEmails.get(userId) || `compte inconnu (${String(userId).slice(0, 8)}…)`;
    },

    /**
     * Say where these figures came from and what is missing from them. A screen
     * that cannot say how fresh it is invites the reader to assume it is fresh.
     * @param {string} source
     * @param {number} count
     */
    renderSourceNotice(source, count) {
        const el = this.elements.sourceNotice;
        if (!el) return;
        const parts = [];
        parts.push(source === 'server'
            ? `${count} événement(s) lus depuis le serveur (lecture paginée complète).`
            : `${count} événement(s) — à la connaissance de cet appareil uniquement : le serveur est injoignable.`);
        if (this.orphanCorrections && this.orphanCorrections.length > 0) {
            parts.push(`${this.orphanCorrections.length} correction(s) sans vente correspondante dans cet ensemble — prévenez l'administrateur.`);
        }
        el.textContent = parts.join(' ');
        el.classList.remove('hidden');
    },

    // ---- projection -------------------------------------------------------

    /**
     * The three lines of a receipt, plus its net. NEVER a single mutated total.
     * @param {{sale:object, discount:object|null, void:object|null}} entry
     */
    linesOf(entry) {
        const gross = Number.isInteger(entry.sale.amount) ? entry.sale.amount : 0;
        const discounted = entry.discount && Number.isInteger(entry.discount.amount) ? entry.discount.amount : 0;
        const voided = entry.void && Number.isInteger(entry.void.amount) ? entry.void.amount : 0;
        return {
            gross,
            discounted,
            voided,
            // Pure summation, exactly as `money/money-fold.js` folds it: nothing
            // downstream special-cases a correction (invariant 10).
            net: gross - discounted - voided,
            isVoided: !!entry.void,
            isDiscounted: !!entry.discount
        };
    },

    /** `'voided' | 'discounted' | 'paid'` — derived, never stored (spec §3.1). */
    statusOf(entry) {
        if (entry.void) return 'voided';
        if (entry.discount) return 'discounted';
        return 'paid';
    },

    /** The printable receipt number: block-issued, or the migrated legacy string. */
    receiptLabel(sale) {
        if (Number.isInteger(sale.receipt_no)) return formatReceiptNo(sale.receipt_no);
        if (sale.legacy_receipt_number) return String(sale.legacy_receipt_number);
        return '—';
    },

    /** The instant a receipt is dated by: the device's clock, else the server's. */
    receiptInstant(sale) {
        return sale.device_time || sale.server_received_at || null;
    },

    // ---- table ------------------------------------------------------------

    renderTable() {
        const body = this.elements.tableBody;
        if (!body) return;

        const rows = this.filteredReceipts();
        body.innerHTML = '';

        if (rows.length === 0) {
            body.innerHTML =
                '<tr><td colspan="8" class="text-center text-muted">Aucun reçu ne correspond à ces filtres</td></tr>';
            return;
        }

        for (const entry of rows) {
            const lines = this.linesOf(entry);
            const status = this.statusOf(entry);
            const statusClass = status === 'voided'
                ? 'badge badge-danger'
                : status === 'discounted' ? 'badge badge-warning' : 'badge badge-success';
            const statusLabel = status === 'voided'
                ? 'Annulé' : status === 'discounted' ? 'Remisé' : 'Payé';

            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td class="font-medium">
                    <button type="button" data-sale-id="${escapeHtml(entry.sale.id)}"
                            style="background:none;border:0;padding:0;color:var(--primary-700,#1B5E20);text-decoration:underline;cursor:pointer;font:inherit;">
                        ${escapeHtml(this.receiptLabel(entry.sale))}
                    </button>
                </td>
                <td class="text-muted">${escapeHtml(formatDateTime(this.receiptInstant(entry.sale)))}</td>
                <td>${escapeHtml(CHANNEL_LABEL[entry.sale.channel] || 'Poste inconnu')}</td>
                <td>${escapeHtml(entry.sale.patient_name || '—')}</td>
                <td class="text-right">${escapeHtml(formatCurrency(lines.gross))}</td>
                <td class="text-right">${lines.discounted > 0 ? escapeHtml('−' + formatCurrency(lines.discounted)) : '—'}</td>
                <td class="text-right font-medium">${escapeHtml(formatCurrency(lines.net))}</td>
                <td><span class="${statusClass}">${escapeHtml(statusLabel)}</span></td>
            `;
            body.appendChild(tr);
        }
    },

    /**
     * Filter the indexed receipts. Every predicate runs FOLD-SIDE (spec §8) — the
     * server was never asked to filter on a dimension a correction does not carry.
     * @returns {object[]}
     */
    filteredReceipts() {
        const type = this.elements.filterType?.value || 'all';
        const status = this.elements.filterStatus?.value || 'all';
        const date = this.elements.filterDate?.value || '';
        const search = (this.elements.searchInput?.value || '').trim().toLowerCase();

        const out = [];
        for (const entry of this.receipts.values()) {
            if (type !== 'all' && entry.sale.channel !== type) continue;
            if (status !== 'all' && this.statusOf(entry) !== status) continue;
            // The SALE's own business date — the day the cash was taken. A void made
            // three days later must not move the sale out of its own day's list.
            if (date && entry.sale.business_date !== date) continue;

            if (search) {
                const haystack = [
                    this.receiptLabel(entry.sale),
                    entry.sale.patient_name || '',
                    this.emailFor(entry.sale.collected_by),
                    entry.sale.book_no || ''
                ].join(' ').toLowerCase();
                if (!haystack.includes(search)) continue;
            }
            out.push(entry);
        }

        // Newest first, by the instant the receipt is dated by.
        out.sort((a, b) => String(this.receiptInstant(b.sale) || '').localeCompare(String(this.receiptInstant(a.sale) || '')));
        return out;
    },

    // ---- details ----------------------------------------------------------

    /**
     * Open one receipt. Reads the ALREADY-LOADED index rather than issuing a second
     * query, so the detail can never disagree with the row that opened it.
     * @param {string|null} saleId
     */
    viewReceipt(saleId) {
        const entry = saleId ? this.receipts.get(saleId) : null;
        if (!entry) {
            showStatus('Ce reçu n\'est plus dans l\'ensemble chargé. Rechargez la liste.', true);
            return;
        }
        this.currentReceipt = entry;
        this.showReceiptDetails(entry);
    },

    /**
     * @param {{sale:object, discount:object|null, void:object|null}} entry
     */
    showReceiptDetails(entry) {
        if (!this.elements.receiptContent) return;
        const { sale } = entry;
        const lines = this.linesOf(entry);

        const items = Array.isArray(sale.items) ? sale.items : [];
        const itemsHtml = items.length > 0
            ? items.map(item => `
                <tr>
                    <td class="px-2 py-1 text-sm">${escapeHtml(item && item.name ? item.name : 'Article non nommé')}</td>
                    <td class="px-2 py-1 text-sm text-center">${escapeHtml(item ? item.qty : '')}</td>
                    <td class="px-2 py-1 text-sm text-right">${escapeHtml(formatCurrency(item ? item.unit_price : 0))}</td>
                    <td class="px-2 py-1 text-sm text-right">${escapeHtml(formatCurrency(item ? item.line_total : 0))}</td>
                </tr>`).join('')
            : `<tr><td colspan="4" class="px-2 py-1 text-sm text-center text-muted">
                   Aucune ligne détaillée sur cet enregistrement (vente antérieure à la réécriture).
               </td></tr>`;

        /**
         * One correction line: amount, motif, author and instant. This is audit M3
         * closed — the reason used to be collected and discarded.
         * @param {object} correction
         * @param {string} label
         * @param {string} colour
         */
        const correctionHtml = (correction, label, colour) => `
            <div class="flex justify-between text-sm mb-1" style="color: ${colour};">
                <span class="font-semibold">${escapeHtml(label)}</span>
                <span>−${escapeHtml(formatCurrency(correction.amount))}</span>
            </div>
            <div class="text-xs mb-2" style="color: ${colour};">
                Motif : ${escapeHtml(correction.reason || 'non renseigné')}<br>
                Par ${escapeHtml(this.emailFor(correction.recorded_by))}
                le ${escapeHtml(formatDateTime(correction.server_received_at || correction.device_time))}
            </div>`;

        const receiptHtml = `
            <div class="receipt-container" id="printable-receipt-body">
                <div class="text-center mb-4 border-b pb-4">
                    <h2 class="text-xl font-bold">CMA OMBESSA</h2>
                    <p class="text-sm text-muted">Centre Médical d'Arrondissement</p>
                </div>

                <div class="mb-4">
                    ${this.detailRow('N° de reçu', this.receiptLabel(sale))}
                    ${this.detailRow('Date', formatDateTime(this.receiptInstant(sale)))}
                    ${this.detailRow('Poste', CHANNEL_LABEL[sale.channel] || 'Poste inconnu')}
                    ${this.detailRow('Encaissé par', this.emailFor(sale.collected_by))}
                    ${sale.collected_by !== sale.recorded_by
                        ? this.detailRow('Enregistré par', this.emailFor(sale.recorded_by))
                        : ''}
                    ${sale.patient_name ? this.detailRow('Patient', sale.patient_name) : ''}
                    ${sale.book_no ? this.detailRow('Carnet papier', sale.book_no) : ''}
                    ${sale.clock_skew_flag
                        ? '<p class="text-xs" style="color: var(--warning-600);">Horloge de l\'appareil décalée au moment de l\'encaissement — la date métier a été fixée par le serveur.</p>'
                        : ''}
                </div>

                <table class="w-full mb-4">
                    <thead>
                        <tr class="border-b">
                            <th class="text-left text-sm font-semibold px-2 py-1">Article</th>
                            <th class="text-center text-sm font-semibold px-2 py-1">Qté</th>
                            <th class="text-right text-sm font-semibold px-2 py-1">P.U.</th>
                            <th class="text-right text-sm font-semibold px-2 py-1">Total</th>
                        </tr>
                    </thead>
                    <tbody>${itemsHtml}</tbody>
                </table>

                <!-- THE SALE, THE DISCOUNT AND THE VOID ARE THREE SEPARATE LINES.
                     The ledger never mutates a total, and neither does this view:
                     what was originally charged stays legible forever. -->
                <div class="border-t pt-2">
                    <div class="flex justify-between text-sm mb-1">
                        <span class="font-semibold">Vente</span>
                        <span>${escapeHtml(formatCurrency(lines.gross))}</span>
                    </div>
                    ${entry.discount ? correctionHtml(entry.discount, 'Remise', 'var(--warning-700, #b45309)') : ''}
                    ${entry.void ? correctionHtml(entry.void, 'Annulation', 'var(--danger-600, #dc2626)') : ''}
                    <div class="flex justify-between text-lg font-bold border-t pt-1">
                        <span>Net</span>
                        <span>${escapeHtml(formatCurrency(lines.net))}</span>
                    </div>
                </div>

                ${entry.void ? `
                <div class="mt-4 text-center">
                    <span class="font-bold text-xl" style="color: var(--danger-600, #dc2626);">ANNULÉ</span>
                </div>` : ''}

                <div class="mt-6 text-center text-xs text-muted">
                    <p>Merci de votre visite — bon rétablissement.</p>
                </div>
            </div>`;

        this.elements.receiptContent.innerHTML = receiptHtml;

        // THE UI OFFERS ONLY WHAT THE SERVER CAN ACCEPT (invariant 10). The server
        // refuses these anyway; a button that is guaranteed to fail is a trap.
        //   • a voided sale takes no further correction — nothing is left to reduce
        //     (`sale-already-voided`), so BOTH actions go away;
        //   • a sale may hold at most one discount (`sale-already-discounted`), but
        //     it may still be voided FOR THE REMAINDER — that is the whole point of
        //     the two-rules-not-one split in invariant 10.
        this.toggle(this.elements.voidBtn, !entry.void);
        this.toggle(this.elements.discountBtn, !entry.void && !entry.discount);

        this.elements.detailsModal?.classList.remove('hidden');
    },

    /**
     * @param {Element|null|undefined} el
     * @param {boolean} visible
     */
    toggle(el, visible) {
        if (!el) return;
        el.classList.toggle('hidden', !visible);
    },

    /**
     * @param {string} label
     * @param {*} value
     * @returns {string}
     */
    detailRow(label, value) {
        return `
            <div class="flex justify-between text-sm mb-1">
                <span class="font-semibold">${escapeHtml(label)} :</span>
                <span>${escapeHtml(value)}</span>
            </div>`;
    },

    // ---- print / email ----------------------------------------------------

    printReceipt() {
        const printContent = document.getElementById('printable-receipt-body');
        if (!printContent) return;
        const printWindow = window.open('', '', 'height=600,width=400');
        if (!printWindow) {
            showStatus('Le navigateur a bloqué la fenêtre d\'impression. Autorisez les fenêtres surgissantes.', true);
            return;
        }

        printWindow.document.write(`<html><head><title>Reçu CMA Ombessa</title><style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { width: 100%; border-collapse: collapse; }
            .text-center { text-align: center; } .text-right { text-align: right; }
            .font-bold, .font-semibold { font-weight: bold; }
            .text-xl { font-size: 1.25rem; } .text-lg { font-size: 1.125rem; }
            .text-sm { font-size: .875rem; } .text-xs { font-size: .75rem; }
            .mb-4 { margin-bottom: 1rem; } .border-b { border-bottom: 1px solid #000; }
            .border-t { border-top: 1px solid #000; }
            .flex { display: flex; } .justify-between { justify-content: space-between; }
        </style></head><body>`);
        printWindow.document.write(printContent.innerHTML);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        // NOT `printWindow.close()` here: closing immediately after `print()`
        // cancels the job in several browsers (audit "Low"). The user closes it.
        showStatus('Reçu envoyé à l\'impression.', false);
    },

    emailReceipt() {
        // Was: prompt for an address, then do nothing, and say nothing about it
        // (audit "Low"). Never ask for something we will not use.
        showStatus('L\'envoi par email n\'est pas encore disponible. Imprimez le reçu ou notez son numéro.', true);
    },

    // ---- corrections: the two online-only admin writes ---------------------

    /**
     * The engine handle, or a French refusal explaining why there is none.
     * @returns {{engine:object}|{error:string}}
     */
    requireOnlineEngine() {
        const engine = getEngine();
        if (!engine || !engine.transport) return { error: messageFor('engine-not-ready') };
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return { error: ONLINE_ONLY };
        return { engine };
    },

    /**
     * Build the correction event. `id` is minted here, once, with
     * `crypto.randomUUID()` (invariant 2): a retry of the same correction replays
     * the same id and heals to `already_present` instead of double-reversing.
     * @param {'void'|'discount'} kind
     * @param {object} sale
     * @param {number} amount
     * @param {string} reason
     * @param {object} engine
     */
    buildCorrection(kind, sale, amount, reason, engine) {
        const event = {
            id: crypto.randomUUID(),
            amount,
            reverses_id: sale.id,
            reason,
            device_time: new Date().toISOString()
        };
        // The admin browser's own device, for audit only. Spec §10.1: the DRAWER a
        // correction is attributed to comes from the TARGET SALE, never from here —
        // the admin's browser never held that cash. `v_cash_events` does that join.
        if (engine.deviceId) event.device_id = engine.deviceId;
        // `kind` is pinned server-side by the RPC wrapper (migration 0005); sending
        // it would be ignored. Named here so a reader knows it is not an omission.
        return event;
    },

    /**
     * Turn the frozen §6 verdict into a screen. `inserted` and `already_present`
     * are BOTH success (invariant 6); anything else is shown with the exact French
     * sentence for the rule that fired, never a green banner.
     * @param {*} verdict
     * @returns {boolean} Whether the correction is on the ledger.
     */
    verdictAccepted(verdict) {
        const status = verdict && verdict.status;
        if (status === 'inserted' || status === 'already_present') return true;
        showStatus(messageFor(verdict && verdict.reason ? verdict.reason : 'conflict'), true);
        return false;
    },

    // ---- void -------------------------------------------------------------

    showVoidForm() {
        const entry = this.currentReceipt;
        if (!entry) return;
        if (entry.void) {
            showStatus(messageFor('sale-already-voided'), true);
            return;
        }
        const lines = this.linesOf(entry);
        if (this.elements.voidReceiptId) this.elements.voidReceiptId.value = this.receiptLabel(entry.sale);
        // A VOID CANCELS WHAT IS LEFT: sale − active discount. The server refuses
        // anything else with `void-amount-must-equal-sale-net-of-discount`, so the
        // field is read-only and computed rather than typed.
        if (this.elements.voidAmount) this.elements.voidAmount.value = formatCurrency(lines.net);
        if (this.elements.voidHint) {
            this.elements.voidHint.textContent = entry.discount
                ? `Solde à annuler : ${formatCurrency(lines.gross)} − ${formatCurrency(lines.discounted)} de remise = ${formatCurrency(lines.net)}.`
                : `Montant de la vente : ${formatCurrency(lines.gross)}.`;
        }
        this.elements.voidModal?.classList.remove('hidden');
    },

    /**
     * @param {Event} e
     */
    async handleVoidSubmit(e) {
        e.preventDefault();
        const entry = this.currentReceipt;
        if (!entry) return;

        // Re-check on submit, not only on open: the details modal may have been
        // sitting open while a pull brought in someone else's void.
        if (entry.void) {
            showStatus(messageFor('sale-already-voided'), true);
            this.closeVoidModal();
            return;
        }

        const reason = (this.elements.voidReason?.value || '').trim();
        if (!reason) {
            showStatus(messageFor('correction-missing-reason'), true);
            return;
        }
        if (reason.length > 500) {
            showStatus(messageFor('reason-too-long'), true);
            return;
        }

        const lines = this.linesOf(entry);
        if (!Number.isInteger(lines.net) || lines.net <= 0) {
            // A sale whose remaining net is zero cannot be voided: `amount > 0`.
            // The server would refuse it; say so plainly instead of trying.
            showStatus(
                'Le solde de cette vente est nul : il n\'y a plus rien à annuler. '
                + 'Vérifiez la remise déjà appliquée.', true);
            return;
        }

        const ready = this.requireOnlineEngine();
        if ('error' in ready) {
            showStatus(ready.error, true);
            return;
        }

        const submit = this.elements.voidForm?.querySelector('button[type="submit"]');
        if (submit) submit.disabled = true;
        try {
            showStatus('Enregistrement de l\'annulation…', false);
            const event = this.buildCorrection('void', entry.sale, lines.net, reason, ready.engine);
            const verdict = await ready.engine.transport.recordVoid(event);
            if (!this.verdictAccepted(verdict)) return;

            showStatus(
                `Annulation enregistrée : ${formatCurrency(lines.net)} sur le reçu ${this.receiptLabel(entry.sale)}. `
                + 'Le stock des médicaments concernés est restitué automatiquement.', false);
            this.closeVoidModal();
            await this.afterCorrection(entry.sale.id, ready.engine);
        } catch (error) {
            console.error('Annulation refusée:', error);
            showStatus(messageForError(error), true);
        } finally {
            if (submit) submit.disabled = false;
        }
    },

    // ---- discount ---------------------------------------------------------

    showDiscountForm() {
        const entry = this.currentReceipt;
        if (!entry) return;
        if (entry.void) {
            showStatus(messageFor('sale-already-voided'), true);
            return;
        }
        if (entry.discount) {
            showStatus(messageFor('sale-already-discounted'), true);
            return;
        }

        const lines = this.linesOf(entry);
        if (this.elements.discountReceiptId) {
            this.elements.discountReceiptId.value = this.receiptLabel(entry.sale);
        }
        // AUDIT M3: this field existed in the markup and was NEVER populated, so the
        // admin discounted blind.
        if (this.elements.discountOriginalAmount) {
            this.elements.discountOriginalAmount.value = formatCurrency(lines.gross);
        }
        if (this.elements.discountAmount) {
            this.elements.discountAmount.value = '';
            this.elements.discountAmount.min = '1';
            // STRICTLY less than the sale total (invariant 10): a 100% reduction is
            // a void and must be recorded as one, or the sale is left with a
            // remaining net of zero and — because `amount > 0` — no legal void.
            this.elements.discountAmount.max = String(Math.max(1, lines.gross - 1));
            this.elements.discountAmount.step = '1';
        }
        if (this.elements.discountHint) {
            this.elements.discountHint.textContent =
                `Montant de la vente : ${formatCurrency(lines.gross)}. `
                + `La remise doit être strictement inférieure à ce montant `
                + `(maximum ${formatCurrency(Math.max(0, lines.gross - 1))}). `
                + 'Pour une réduction totale, enregistrez une annulation.';
        }
        this.elements.discountModal?.classList.remove('hidden');
    },

    /**
     * @param {Event} e
     */
    async handleDiscountSubmit(e) {
        e.preventDefault();
        const entry = this.currentReceipt;
        if (!entry) return;

        if (entry.void) {
            showStatus(messageFor('sale-already-voided'), true);
            this.closeDiscountModal();
            return;
        }
        if (entry.discount) {
            showStatus(messageFor('sale-already-discounted'), true);
            this.closeDiscountModal();
            return;
        }

        const amount = parseWholeFcfa(this.elements.discountAmount?.value);
        const reason = (this.elements.discountReason?.value || '').trim();
        const lines = this.linesOf(entry);

        if (amount === null || amount <= 0) {
            showStatus(messageFor('amount-must-be-positive-integer'), true);
            return;
        }
        if (amount >= lines.gross) {
            showStatus(messageFor('discount-exceeds-sale-amount'), true);
            return;
        }
        if (!reason) {
            showStatus(messageFor('correction-missing-reason'), true);
            return;
        }
        if (reason.length > 500) {
            showStatus(messageFor('reason-too-long'), true);
            return;
        }

        const ready = this.requireOnlineEngine();
        if ('error' in ready) {
            showStatus(ready.error, true);
            return;
        }

        const submit = this.elements.discountForm?.querySelector('button[type="submit"]');
        if (submit) submit.disabled = true;
        try {
            showStatus('Enregistrement de la remise…', false);
            const event = this.buildCorrection('discount', entry.sale, amount, reason, ready.engine);
            const verdict = await ready.engine.transport.recordDiscount(event);
            if (!this.verdictAccepted(verdict)) return;

            showStatus(
                `Remise enregistrée : ${formatCurrency(amount)} sur le reçu ${this.receiptLabel(entry.sale)}. `
                + `Reste dû : ${formatCurrency(lines.gross - amount)}.`, false);
            this.closeDiscountModal();
            await this.afterCorrection(entry.sale.id, ready.engine);
        } catch (error) {
            console.error('Remise refusée:', error);
            showStatus(messageForError(error), true);
        } finally {
            if (submit) submit.disabled = false;
        }
    },

    /**
     * Post-commit refresh. Deliberately OUTSIDE the try that reports submission
     * failure (audit M5): a failing refresh must never announce "échec" for a
     * correction the server has already accepted and the admin would then repeat.
     *
     * `kick()` pushes and PULLS, so the new correction reaches this device's mirror
     * — and, on their next round, the collecting device's (harness F16), where its
     * stock fold and expected cash heal on their own.
     * @param {string} saleId
     * @param {object} engine
     */
    async afterCorrection(saleId, engine) {
        try {
            await engine.kick();
        } catch (error) {
            console.warn('Synchronisation post-correction différée:', error);
        }
        try {
            await this.loadReceipts();
            const entry = this.receipts.get(saleId);
            if (entry) {
                this.currentReceipt = entry;
                this.showReceiptDetails(entry);
            }
            if (window.AppCore && window.AppCore.refreshDrugs) await window.AppCore.refreshDrugs();
        } catch (error) {
            console.warn('Rafraîchissement de la liste des reçus différé:', error);
        }
    }
};

// Kept for backwards compatibility with any inline handler that still names it.
// Nothing in `index.html` calls into it any more — the table binds its actions with
// `addEventListener` and a `data-sale-id`, so no receipt id or patient name is ever
// interpolated into an attribute (audit M7).
window.ReceiptManagement = ReceiptManagement;
